import { api, extractErrorMessage } from "./api";

/**
 * File d'attente locale des pointages — chantiers en zone blanche.
 *
 * POURQUOI : en sous-sol, en tunnel ou sur un chantier sans couverture, l'appel
 * `/punch/in` échoue et l'heure de l'ouvrier est simplement perdue. Il la
 * retrouve fausse en fin de mois, et le conducteur de travaux paie — ou refuse —
 * des heures qu'il ne peut plus justifier. Ici le geste est écrit sur l'appareil
 * AVEC SON HORODATAGE RÉEL, puis rejoué dès que le réseau revient : l'heure
 * inscrite en paie est celle du geste, jamais celle de l'envoi.
 *
 * Le module ne dépend que de l'instance axios : il est utilisable depuis
 * n'importe quel écran et testable hors React.
 */

export const PUNCH_QUEUE_STORAGE_KEY = "flowbtp_punch_queue_v1";

/** Tag de Background Sync (Chromium) — cf. public/service-worker.js. */
export const PUNCH_SYNC_TAG = "knowrok-punch-sync";

/** Message envoyé par le service worker aux onglets pour réveiller la file. */
export const PUNCH_FLUSH_MESSAGE = "knowrok-flush-punch-queue";

// Un échec réseau se répète rarement seul : on espace les tentatives pour ne pas
// vider la batterie du téléphone au fond d'un parking.
const RETRY_BASE_MS = 30 * 1000;
const RETRY_MAX_MS = 10 * 60 * 1000;
const AUTO_FLUSH_INTERVAL_MS = 60 * 1000;

// ---------------------------------------------------------------------------
// Horodatage local
// ---------------------------------------------------------------------------

function pad2(n) {
  return String(n).padStart(2, "0");
}

function asDate(value) {
  return value instanceof Date ? value : new Date(value);
}

/** "HH:MM" de l'heure locale de l'appareil — l'heure du geste. */
export function localHHMM(value) {
  const d = asDate(value);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * Même arrondi que le serveur (`_round_down_15`). Sert uniquement à comparer :
 * si l'envoi tombe dans le même quart d'heure que le geste, le serveur écrira
 * de lui-même la bonne valeur et on le laisse faire — le pointage en ligne
 * garde alors exactement le comportement d'aujourd'hui.
 */
export function roundDownQuarter(value) {
  const d = asDate(value);
  return `${pad2(d.getHours())}:${pad2(Math.floor(d.getMinutes() / 15) * 15)}`;
}

/** "YYYY-MM-DD" local. */
export function localDateISO(value) {
  const d = asDate(value);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Identifiant du pointage généré côté client : il est posé au moment du geste,
 * survit au rechargement de l'app et sert de clé d'idempotence — deux
 * synchronisations parties en même temps ne peuvent pas créer deux pointages.
 */
export function newPunchId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* contexte non sécurisé : on retombe sur l'identifiant maison */
  }
  return `punch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Stockage
// ---------------------------------------------------------------------------

function storage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_e) {
    return null;
  }
}

function readAll() {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(PUNCH_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Une file corrompue ne doit pas casser l'écran : on repart d'une liste vide
    // plutôt que de laisser une exception remonter dans le rendu.
    return Array.isArray(parsed) ? parsed.filter((it) => it && it.id && it.site_id) : [];
  } catch (_e) {
    return [];
  }
}

function writeAll(items) {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(PUNCH_QUEUE_STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch (_e) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Abonnement (bandeau d'en-tête, bouton de pointage)
// ---------------------------------------------------------------------------

const listeners = new Set();

function notify() {
  for (const fn of listeners) {
    try {
      fn();
    } catch (_e) {
      /* un abonné cassé ne doit pas empêcher les autres d'être prévenus */
    }
  }
}

export function subscribePunchQueue(fn) {
  if (typeof fn !== "function") return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Propriétaire de la file
// ---------------------------------------------------------------------------

// localStorage est partagé par tous les comptes ouverts sur le téléphone. Sans
// ce filtre, le pointage laissé par un ouvrier partirait sous le jeton du
// suivant qui se connecte — et atterrirait sur la mauvaise feuille d'heures.
let ownerUserId = null;

export function setPunchQueueOwner(userId) {
  ownerUserId = userId || null;
}

export function getPunchQueueOwner() {
  return ownerUserId;
}

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

function byCapturedAt(a, b) {
  return String(a.captured_at).localeCompare(String(b.captured_at));
}

/** Pointages en attente de CET utilisateur, du plus ancien au plus récent. */
export function getPunchQueue(userId = ownerUserId) {
  if (!userId) return [];
  return readAll()
    .filter((it) => it.user_id === userId)
    .sort(byCapturedAt);
}

/** Ce que l'ouvrier doit voir : « il me reste N pointages à envoyer ». */
export function countPendingPunches(userId = ownerUserId) {
  return getPunchQueue(userId).filter((it) => it.status !== "blocked").length;
}

/** Pointages qu'un envoi automatique ne débloquera pas : ils exigent un humain. */
export function getBlockedPunches(userId = ownerUserId) {
  return getPunchQueue(userId).filter((it) => it.status === "blocked");
}

// ---------------------------------------------------------------------------
// Écriture
// ---------------------------------------------------------------------------

function patchItem(id, patch) {
  const all = readAll();
  const idx = all.findIndex((it) => it.id === id);
  if (idx === -1) return false;
  all[idx] = { ...all[idx], ...patch };
  if (!writeAll(all)) return false;
  notify();
  return true;
}

function dropItem(id) {
  const all = readAll();
  const next = all.filter((it) => it.id !== id);
  if (next.length === all.length) return false;
  if (!writeAll(next)) return false;
  notify();
  return true;
}

/**
 * Enregistre un pointage sur l'appareil. Lève si le stockage est indisponible :
 * mieux vaut dire « non enregistré » que promettre à tort — c'est exactement la
 * promesse creuse qu'on cherche à supprimer.
 */
export function enqueuePunch({
  id,
  kind,
  siteId,
  siteName,
  userId,
  token,
  capturedAt,
  lat,
  lng,
  accuracy,
} = {}) {
  if (kind !== "in" && kind !== "out") {
    throw new Error("Type de pointage inconnu — pointage non enregistré");
  }
  if (!siteId) throw new Error("Chantier manquant — pointage non enregistré");
  const owner = userId || ownerUserId;
  if (!owner) throw new Error("Session introuvable — pointage non enregistré");

  const at = capturedAt ? asDate(capturedAt) : new Date();
  const item = {
    id: id || newPunchId(),
    user_id: owner,
    kind,
    site_id: siteId,
    site_name: siteName || "",
    token: token || null,
    captured_at: at.toISOString(),
    local_time: localHHMM(at),
    local_date: localDateISO(at),
    lat: typeof lat === "number" ? lat : null,
    lng: typeof lng === "number" ? lng : null,
    accuracy: typeof accuracy === "number" ? accuracy : null,
    status: "pending",
    attempts: 0,
    next_attempt_at: 0,
    error: null,
    punch_landed: false,
  };

  const all = readAll();
  // Idempotence : réenregistrer le même identifiant ne crée pas un doublon.
  const existing = all.find((it) => it.id === item.id);
  if (existing) return existing;

  all.push(item);
  if (!writeAll(all)) {
    throw new Error("Stockage de l'appareil saturé — pointage non enregistré");
  }
  notify();
  requestBackgroundSync();
  return item;
}

/**
 * Retire un pointage bloqué que l'ouvrier a lu et pris en charge. Un
 * pointage encore « pending » n'est jamais supprimable : rien ne disparaît sans
 * qu'on le décide.
 */
export function dismissPunch(id) {
  const item = readAll().find((it) => it.id === id);
  if (!item || item.status !== "blocked") return false;
  return dropItem(id);
}

// ---------------------------------------------------------------------------
// Diagnostic réseau
// ---------------------------------------------------------------------------

/**
 * Vrai quand l'appel n'a jamais atteint le serveur : coupure, tunnel, sous-sol,
 * délai dépassé. C'est le seul cas où un geste a le droit de partir en file
 * locale — si le serveur a répondu, même 500, il a vu la demande et c'est à lui
 * de trancher, pas à l'appareil.
 */
export function estPanneReseau(error) {
  if (error?.response) return false;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const code = error?.code;
  if (code === "ERR_NETWORK" || code === "ECONNABORTED" || code === "ETIMEDOUT") return true;
  // axios pose `request` dès que l'appel est parti sans jamais recevoir de réponse.
  return !!error?.request;
}

// ---------------------------------------------------------------------------
// Envoi
// ---------------------------------------------------------------------------

/**
 * Un échec sans réponse HTTP (réseau coupé, délai dépassé) ou un 5xx sera
 * peut-être résolu tout seul : on réessaie. Un 4xx métier (chantier en
 * brouillon, absence approuvée, heure future) ne se résoudra jamais par une
 * nouvelle tentative — on le montre à l'utilisateur au lieu de boucler.
 *
 * BUG-002 — une exception : le 409 « pointage déjà ouvert sur un autre
 * chantier ». Celui-là SE résout, dès que l'ouvrier se dépointe. Le classer
 * définitif mettrait la ligne en `blocked`, état d'où la file ne repart jamais
 * seule : le geste resterait à l'écran sans jamais atteindre le serveur, ce
 * qui est précisément la perte que la file existe pour empêcher. Il reste donc
 * `pending`, avec son message, et repart au dépointage.
 */
function isRetriable(error) {
  const status = error?.response?.status;
  if (status === undefined || status === null) return true;
  if (status >= 500) return true;
  if (status === 409 && error?.response?.data?.detail?.kind === "open_other_site") return true;
  return status === 401 || status === 408 || status === 429;
}

function backoffMs(attempts) {
  return Math.min(RETRY_BASE_MS * 2 ** Math.max(0, attempts - 1), RETRY_MAX_MS);
}

function baseBody(item) {
  const body = {
    site_id: item.site_id,
    lat: item.lat ?? null,
    lng: item.lng ?? null,
    accuracy: item.accuracy ?? null,
    // Clé d'idempotence côté client. Le backend la LIT depuis BUG-026, sur
    // /punch/in comme sur /punch/out : rejouer le même geste retombe sur la
    // feuille déjà créée au lieu d'en ouvrir une seconde. L'écran de pointage
    // envoie la même clé sur son appel direct, pour qu'un geste parti en ligne
    // puis mis en file ne puisse pas atterrir deux fois.
    client_punch_id: item.id,
  };
  if (item.token) body.token = item.token;
  return body;
}

async function sendPunchIn(item, now) {
  const body = baseBody(item);
  // Envoi immédiat : on laisse le serveur horodater comme aujourd'hui (arrondi
  // au quart d'heure) — le pointage en ligne ne change pas d'un iota.
  // Envoi différé : on impose l'heure du geste, sinon un pointage de 7h02
  // remonté à 11h serait payé 11h.
  if (roundDownQuarter(item.captured_at) !== roundDownQuarter(now)) {
    body.custom_start_time = item.local_time;
  }
  const { data } = await api.post("/punch/in", body);
  return { data, warning: null };
}

async function sendPunchOut(item, now) {
  // Le serveur accepte désormais l'heure du geste : un départ de 17h07 remonté
  // à 19h32 reste payé 17h07. Il la borne lui-même — jamais avant l'arrivée,
  // jamais après la réception — et trace l'écart dans l'audit du pointage.
  const { data } = await api.post("/punch/out", {
    ...baseBody(item),
    actual_end_time: item.local_time,
  });
  const serverEnd = data?.end_time || null;
  const expected = item.local_time;

  // Repli : si le serveur n'a pas retenu l'heure du geste (version antérieure,
  // ou heure hors bornes), on tente la correction de pointage existante plutôt
  // que de laisser passer une fin de journée fausse.
  if (data?.id && serverEnd && serverEnd !== expected) {
    if (!data.start_time || item.local_time <= data.start_time) {
      return {
        data,
        warning:
          `Départ envoyé, mais l'heure réelle (${item.local_time}) est antérieure ou égale à l'arrivée : ` +
          `le serveur a retenu ${serverEnd}. À régulariser avec votre responsable.`,
      };
    }
    try {
      await api.patch(`/punch/${data.id}/edit`, {
        end_time: item.local_time,
        edit_reason: `Pointage hors ligne — départ réel à ${item.local_time}, envoyé à ${localHHMM(now)}`,
      });
    } catch (_e) {
      return {
        data,
        warning:
          `Départ envoyé, mais l'heure réelle (${item.local_time}) n'a pas pu être appliquée : ` +
          `le serveur a retenu ${serverEnd}. Signalez-le à votre responsable.`,
      };
    }
  }
  return { data, warning: null };
}

// ---------------------------------------------------------------------------
// Vidage de la file
// ---------------------------------------------------------------------------

let flushing = false;

export function isPunchQueueSyncing() {
  return flushing;
}

/**
 * Rejoue les pointages en attente, du plus ancien au plus récent.
 *
 * Règles :
 * - un envoi réussi sort de la file, tout le reste y demeure ;
 * - deux appels concurrents ne peuvent pas partir en même temps (verrou) ;
 * - dès qu'un pointage d'un chantier échoue, les suivants du même chantier
 *   attendent : une sortie ne doit jamais partir avant son arrivée.
 */
export async function flushPunchQueue({ userId = ownerUserId, force = false } = {}) {
  const summary = {
    sent: 0,
    kept: 0,
    blocked: 0,
    already_open: 0,
    skipped: false,
    offline: false,
    warnings: [],
  };

  if (flushing) {
    summary.skipped = true;
    return summary;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    summary.offline = true;
    return summary;
  }

  const items = getPunchQueue(userId).filter((it) => it.status === "pending");
  if (items.length === 0) return summary;

  flushing = true;
  notify();
  try {
    const now = new Date();
    const today = localDateISO(now);
    const haltedSites = new Set();

    for (const item of items) {
      if (haltedSites.has(item.site_id)) {
        summary.kept += 1;
        continue;
      }
      if (!force && item.next_attempt_at && Date.now() < item.next_attempt_at) {
        summary.kept += 1;
        haltedSites.add(item.site_id);
        continue;
      }
      // Le serveur ne sait poser un pointage qu'à la date du jour : rejouer un
      // geste de la veille l'inscrirait aujourd'hui, sur la mauvaise journée de
      // paie. On le garde visible et on demande une régularisation.
      if (item.local_date !== today) {
        const jour = asDate(item.captured_at).toLocaleDateString("fr-FR");
        patchItem(item.id, {
          status: "blocked",
          error:
            `Pointage du ${jour} à ${item.local_time} : il ne peut plus être envoyé automatiquement ` +
            `(il serait enregistré à la date d'aujourd'hui). À saisir avec votre responsable.`,
        });
        summary.blocked += 1;
        haltedSites.add(item.site_id);
        continue;
      }

      try {
        const { data, warning } =
          item.kind === "in" ? await sendPunchIn(item, now) : await sendPunchOut(item, now);
        if (warning) {
          // Le pointage est bien parti : on ne le renverra pas (pas de doublon),
          // mais on garde la ligne à l'écran tant que l'écart n'est pas traité.
          patchItem(item.id, { status: "blocked", punch_landed: true, error: warning });
          summary.blocked += 1;
          summary.warnings.push(warning);
        } else {
          // `already_open` : le serveur avait déjà ce pointage ouvert. Une
          // synchronisation partie deux fois n'a donc rien créé de nouveau —
          // c'est la garantie « pas de doublon », côté serveur cette fois.
          if (data?.already_open) summary.already_open += 1;
          dropItem(item.id);
          summary.sent += 1;
        }
      } catch (e) {
        const message = extractErrorMessage(e, "Envoi impossible pour le moment");
        const attempts = (item.attempts || 0) + 1;
        if (isRetriable(e)) {
          patchItem(item.id, {
            attempts,
            next_attempt_at: Date.now() + backoffMs(attempts),
            error: message,
            status: "pending",
          });
          summary.kept += 1;
          haltedSites.add(item.site_id);
          if (!e?.response) {
            // Plus de réseau du tout : inutile d'essayer les suivants.
            summary.offline = true;
            break;
          }
        } else {
          patchItem(item.id, { attempts, status: "blocked", error: message });
          summary.blocked += 1;
          haltedSites.add(item.site_id);
        }
      }
    }
  } finally {
    flushing = false;
    notify();
  }
  return summary;
}

// ---------------------------------------------------------------------------
// Réveil automatique
// ---------------------------------------------------------------------------

function requestBackgroundSync() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    navigator.serviceWorker.ready
      .then((reg) => reg?.sync?.register(PUNCH_SYNC_TAG))
      .catch(() => {});
  } catch (_e) {
    /* Background Sync absent (Safari/Firefox) : le repli périodique suffit */
  }
}

let autoFlushInstalled = false;

/**
 * Installe les déclencheurs de vidage. Idempotent : appelé par le shell de
 * l'application ET par le bouton de pointage, car l'écran de pointage
 * (`/sites/:id/punch`) vit hors du shell — sans cela, un ouvrier qui reste sur
 * la badgeuse garderait ses heures bloquées sur son téléphone.
 */
export function ensurePunchQueueAutoFlush() {
  if (typeof window === "undefined" || autoFlushInstalled) return;
  autoFlushInstalled = true;

  // Retour du réseau : on force, sans attendre le délai de repli.
  window.addEventListener("online", () => {
    flushPunchQueue({ force: true });
  });

  // Le téléphone sort de la poche : c'est souvent là que le réseau est revenu.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flushPunchQueue();
  });

  // Filet : `navigator.onLine` reste à true sur un réseau présent mais muet.
  window.setInterval(() => {
    flushPunchQueue();
  }, AUTO_FLUSH_INTERVAL_MS);

  // Un autre onglet a pointé : on rafraîchit le compteur affiché ici.
  window.addEventListener("storage", (event) => {
    if (!event || event.key === null || event.key === PUNCH_QUEUE_STORAGE_KEY) notify();
  });

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener?.("message", (event) => {
      if (event?.data?.type === PUNCH_FLUSH_MESSAGE) flushPunchQueue({ force: true });
    });
  }

  // Un pointage peut dormir dans la file depuis la session précédente.
  flushPunchQueue();
}

// ===========================================================================
// File d'attente locale des mémos
//
// POURQUOI : le mémo est le geste de terrain par excellence — « gaine percée
// au R+2, lot 3 » se note devant le problème, c'est-à-dire au fond du bâtiment,
// là où il n'y a pas de réseau. Jusqu'ici l'envoi échouait et la saisie
// disparaissait avec le toast d'erreur : l'information n'existait plus nulle
// part. Même mécanique que les pointages ci-dessus : écrit sur l'appareil,
// rejoué au retour du réseau vers l'endpoint de création normal.
//
// CONTRAINTE : localStorage ne stocke que du texte, avec un quota de quelques
// mégaoctets partagé par toute l'application. Les photos et les notes vocales
// passent par un upload multipart (/uploads/feed-media) : elles ne peuvent pas
// être mises en file. L'écran de saisie doit le DIRE au lieu de les perdre en
// silence — cf. MemoDialog.jsx.
// ===========================================================================

export const MEMO_QUEUE_STORAGE_KEY = "flowbtp_memo_queue_v1";

/** Identifiant client du mémo : clé d'idempotence posée au moment de la saisie. */
export function newMemoId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* contexte non sécurisé : on retombe sur l'identifiant maison */
  }
  return `memo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Propriétaire de la file des mémos — volontairement le MÊME que celui des
 * pointages : localStorage est partagé par tous les comptes ouverts sur le
 * téléphone, et un mémo ne doit jamais partir sous le jeton de l'ouvrier
 * suivant, ni s'afficher dans son compteur.
 */
export function setMemoQueueOwner(userId) {
  setPunchQueueOwner(userId);
}

function readAllMemos() {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(MEMO_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Une file corrompue ne doit pas casser l'écran des mémos.
    return Array.isArray(parsed)
      ? parsed.filter((it) => it && it.id && it.site_id && it.payload)
      : [];
  } catch (_e) {
    return [];
  }
}

function writeAllMemos(items) {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(MEMO_QUEUE_STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch (_e) {
    return false;
  }
}

const memoListeners = new Set();

function notifyMemos() {
  for (const fn of memoListeners) {
    try {
      fn();
    } catch (_e) {
      /* un abonné cassé ne doit pas empêcher les autres d'être prévenus */
    }
  }
}

export function subscribeMemoQueue(fn) {
  if (typeof fn !== "function") return () => {};
  memoListeners.add(fn);
  return () => memoListeners.delete(fn);
}

/** Mémos en attente de CET utilisateur, du plus ancien au plus récent. */
export function getMemoQueue(userId = ownerUserId) {
  if (!userId) return [];
  return readAllMemos()
    .filter((it) => it.user_id === userId)
    .sort(byCapturedAt);
}

/** Ce que l'utilisateur doit voir : « il me reste N mémos à envoyer ». */
export function countPendingMemos(userId = ownerUserId) {
  return getMemoQueue(userId).filter((it) => it.status !== "blocked").length;
}

/** Mémos qu'un envoi automatique ne débloquera pas : ils exigent un humain. */
export function getBlockedMemos(userId = ownerUserId) {
  return getMemoQueue(userId).filter((it) => it.status === "blocked");
}

function patchMemoItem(id, patch) {
  const all = readAllMemos();
  const idx = all.findIndex((it) => it.id === id);
  if (idx === -1) return false;
  all[idx] = { ...all[idx], ...patch };
  if (!writeAllMemos(all)) return false;
  notifyMemos();
  return true;
}

function dropMemoItem(id) {
  const all = readAllMemos();
  const next = all.filter((it) => it.id !== id);
  if (next.length === all.length) return false;
  if (!writeAllMemos(next)) return false;
  notifyMemos();
  return true;
}

/**
 * Enregistre un mémo sur l'appareil. `payload` est le corps exact attendu par
 * POST /sites/{site_id}/memos (titre, corps, couleur, zone, échéance, mentions,
 * épinglage…). Lève si le stockage est indisponible : mieux vaut dire « non
 * enregistré » que promettre à tort.
 */
export function enqueueMemo({ id, siteId, siteName, userId, payload, capturedAt } = {}) {
  if (!siteId) throw new Error("Chantier manquant — mémo non enregistré");
  if (!payload || !payload.title) throw new Error("Titre manquant — mémo non enregistré");
  const owner = userId || ownerUserId;
  if (!owner) throw new Error("Session introuvable — mémo non enregistré");

  const at = capturedAt ? asDate(capturedAt) : new Date();
  const item = {
    id: id || newMemoId(),
    user_id: owner,
    site_id: siteId,
    site_name: siteName || "",
    // Texte seul : on écrase toute référence média pour qu'un appelant distrait
    // ne mette pas un blob dans localStorage (quota) ni une URL locale morte.
    payload: { ...payload, photo_urls: [], photo_url: null, voice_url: null },
    captured_at: at.toISOString(),
    status: "pending",
    attempts: 0,
    next_attempt_at: 0,
    error: null,
  };

  const all = readAllMemos();
  // Idempotence : réenregistrer le même identifiant ne crée pas un doublon.
  const existing = all.find((it) => it.id === item.id);
  if (existing) return existing;

  all.push(item);
  if (!writeAllMemos(all)) {
    throw new Error("Stockage de l'appareil saturé — mémo non enregistré");
  }
  notifyMemos();
  return item;
}

/**
 * Retire un mémo bloqué que l'utilisateur a lu et pris en charge. Un mémo
 * encore « pending » n'est jamais supprimable : rien ne disparaît sans qu'on
 * le décide.
 */
export function dismissMemo(id) {
  const item = readAllMemos().find((it) => it.id === id);
  if (!item || item.status !== "blocked") return false;
  return dropMemoItem(id);
}

let flushingMemos = false;

export function isMemoQueueSyncing() {
  return flushingMemos;
}

/**
 * Rejoue les mémos en attente, du plus ancien au plus récent.
 *
 * Règles :
 * - un mémo ne sort de la file qu'après une réponse 2xx (axios lève sur tout le
 *   reste) : un envoi incertain reste en attente plutôt que d'être perdu ;
 * - deux appels concurrents ne peuvent pas partir en même temps (verrou) ;
 * - un refus métier (module mémos désactivé, accès au chantier retiré) passe en
 *   « blocked » : le rejouer indéfiniment n'y changerait rien.
 */
export async function flushMemoQueue({ userId = ownerUserId, force = false } = {}) {
  const summary = { sent: 0, kept: 0, blocked: 0, skipped: false, offline: false };

  if (flushingMemos) {
    summary.skipped = true;
    return summary;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    summary.offline = true;
    return summary;
  }

  const items = getMemoQueue(userId).filter((it) => it.status === "pending");
  if (items.length === 0) return summary;

  flushingMemos = true;
  notifyMemos();
  try {
    for (const item of items) {
      if (!force && item.next_attempt_at && Date.now() < item.next_attempt_at) {
        summary.kept += 1;
        continue;
      }
      try {
        // `client_memo_id` : clé d'idempotence côté client. Le backend l'ignore
        // aujourd'hui (Pydantic laisse tomber les champs inconnus) ; elle est
        // envoyée pour qu'il puisse dédoublonner sans changer le contrat le jour
        // où il la lira. En attendant, le garde-fou est la règle « on ne retire
        // de la file qu'après un 2xx ».
        await api.post(`/sites/${item.site_id}/memos`, {
          ...item.payload,
          client_memo_id: item.id,
        });
        dropMemoItem(item.id);
        summary.sent += 1;
      } catch (e) {
        const message = extractErrorMessage(e, "Envoi impossible pour le moment");
        const attempts = (item.attempts || 0) + 1;
        if (isRetriable(e)) {
          patchMemoItem(item.id, {
            attempts,
            next_attempt_at: Date.now() + backoffMs(attempts),
            error: message,
            status: "pending",
          });
          summary.kept += 1;
          if (!e?.response) {
            // Plus de réseau du tout : inutile d'essayer les suivants.
            summary.offline = true;
            break;
          }
        } else {
          patchMemoItem(item.id, { attempts, status: "blocked", error: message });
          summary.blocked += 1;
        }
      }
    }
  } finally {
    flushingMemos = false;
    notifyMemos();
  }
  return summary;
}

let memoAutoFlushInstalled = false;

/**
 * Installe les déclencheurs de vidage des mémos. Idempotent : appelé par
 * l'écran de saisie ET par la liste des mémos, car aucun des deux n'est garanti
 * d'être monté quand le réseau revient.
 */
export function ensureMemoQueueAutoFlush() {
  if (typeof window === "undefined" || memoAutoFlushInstalled) return;
  memoAutoFlushInstalled = true;

  // Retour du réseau : on force, sans attendre le délai de repli.
  window.addEventListener("online", () => {
    flushMemoQueue({ force: true });
  });

  // Le téléphone sort de la poche : c'est souvent là que le réseau est revenu.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flushMemoQueue();
  });

  // Filet : `navigator.onLine` reste à true sur un réseau présent mais muet.
  window.setInterval(() => {
    flushMemoQueue();
  }, AUTO_FLUSH_INTERVAL_MS);

  // Un autre onglet a saisi un mémo : on rafraîchit le compteur affiché ici.
  window.addEventListener("storage", (event) => {
    if (!event || event.key === null || event.key === MEMO_QUEUE_STORAGE_KEY) notifyMemos();
  });

  // Un mémo peut dormir dans la file depuis la session précédente.
  flushMemoQueue();
}

// ===========================================================================
// Rapport d'intervention — brouillon local + file d'envoi
//
// POURQUOI : le rapport d'intervention se saisit là où l'intervention a lieu —
// local technique en sous-sol, chaufferie, parking couvert. Le technicien y
// passe dix minutes à décrire un diagnostic, puis un trou réseau (ou un
// téléphone qui se verrouille, ou un appel entrant qui recharge l'onglet)
// efface la saisie entière : il n'en reste rien, ni sur l'appareil ni sur le
// serveur. Deux filets, indépendants l'un de l'autre :
//
//   1. BROUILLON — l'état du formulaire est écrit sur l'appareil au fil de la
//      saisie (une clé par intervention) et relu à la réouverture de l'écran.
//      Il est purgé à la soumission réussie, et lui seul.
//   2. FILE D'ENVOI — si la soumission échoue faute de réseau (`estPanneReseau`,
//      même mécanique que les pointages et les mémos), l'appel est écrit sur
//      l'appareil et rejoué au retour du réseau.
//
// CONTRAINTE : localStorage ne stocke que du texte, avec un quota de quelques
// mégaoctets partagé par toute l'application. Les photos d'un rapport passent
// par un upload multipart et pèsent plusieurs mégaoctets à elles seules : elles
// ne sont NI mises en brouillon NI mises en file. L'écran doit le DIRE
// (« les photos devront être renvoyées ») au lieu de les perdre en silence —
// c'est la seule chose pire qu'un envoi raté.
// ===========================================================================

/** Préfixe des brouillons — une clé de stockage par intervention. */
export const INTERVENTION_REPORT_DRAFT_PREFIX = "flowbtp_report_draft_v1:";

export const INTERVENTION_REPORT_QUEUE_STORAGE_KEY = "flowbtp_report_queue_v1";

// Écriture différée : le technicien tape, on n'écrit qu'une fois la frappe
// retombée. Sans ce délai, chaque caractère sérialise le formulaire entier sur
// le fil principal — la saisie devient hachée sur un téléphone de chantier.
const DRAFT_DEBOUNCE_MS = 1000;

// Un brouillon d'il y a plus d'une semaine ne correspond plus à rien sur le
// terrain : le restaurer ferait signer au client un rapport périmé.
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Garde-fous de quota : un brouillon ou un envoi ne doit jamais saturer à lui
// seul le stockage partagé avec les pointages et les mémos — des heures de
// travail y dorment.
const DRAFT_MAX_BYTES = 400 * 1024;
const REPORT_PAYLOAD_MAX_BYTES = 512 * 1024;

// Champs porteurs de photos : jamais persistés (cf. contrainte ci-dessus).
const PHOTO_FIELD_KEYS = [
  "photos",
  "photos_before",
  "photos_after",
  "photo_urls",
  "photo_url",
  "files",
  "attachments",
];

/** Identifiant client d'un envoi de rapport : clé d'idempotence. */
export function newInterventionReportId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* contexte non sécurisé : on retombe sur l'identifiant maison */
  }
  return `report-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Propriétaire des brouillons et de la file des rapports — volontairement le
 * MÊME que celui des pointages : localStorage est partagé par tous les comptes
 * ouverts sur le téléphone, et le rapport d'un technicien ne doit ni partir
 * sous le jeton du suivant, ni réapparaître dans son formulaire.
 */
export function setInterventionReportQueueOwner(userId) {
  setPunchQueueOwner(userId);
}

// ---------------------------------------------------------------------------
// Brouillon
// ---------------------------------------------------------------------------

function draftKey(interventionId) {
  return `${INTERVENTION_REPORT_DRAFT_PREFIX}${interventionId}`;
}

/**
 * Ne garde du formulaire que ce qui survit à un aller-retour JSON. Un File ou
 * un Blob s'y transformerait en `{}` : le brouillon restauré afficherait alors
 * une photo fantôme que rien ne pourrait plus envoyer. On les retire, et on
 * compte les retraits pour que l'écran puisse en avertir l'utilisateur.
 */
function sanitizeForStorage(value, dropped = { photos: 0 }, seen = new Set(), depth = 0) {
  if (value === null || value === undefined) return undefined;
  const type = typeof value;
  if (type === "string" || type === "number" || type === "boolean") {
    return Number.isNaN(value) ? undefined : value;
  }
  if (type === "function" || type === "symbol" || type === "bigint") return undefined;
  if (typeof Blob !== "undefined" && value instanceof Blob) {
    // File hérite de Blob : les deux tombent ici.
    dropped.photos += 1;
    return undefined;
  }
  if (value instanceof Date) return value.toISOString();
  // Profondeur bornée + détection de cycle : un formulaire porte parfois une
  // référence DOM ou une structure circulaire, JSON.stringify lèverait.
  if (depth > 6 || seen.has(value)) return undefined;
  seen.add(value);
  try {
    if (Array.isArray(value)) {
      return value
        .map((v) => sanitizeForStorage(v, dropped, seen, depth + 1))
        .filter((v) => v !== undefined);
    }
    if (type === "object") {
      const out = {};
      for (const [k, v] of Object.entries(value)) {
        const clean = sanitizeForStorage(v, dropped, seen, depth + 1);
        if (clean !== undefined) out[k] = clean;
      }
      return out;
    }
  } finally {
    seen.delete(value);
  }
  return undefined;
}

/**
 * Écrit le brouillon d'un rapport. Retourne `{ saved, photosDropped }` :
 * `saved` à false quand le stockage est indisponible ou saturé — l'écran ne
 * doit jamais promettre un brouillon qui n'existe pas.
 */
export function saveInterventionReportDraft(interventionId, values, meta = {}) {
  const result = { saved: false, photosDropped: 0 };
  if (!interventionId) return result;
  const store = storage();
  if (!store) return result;

  const dropped = { photos: 0 };
  const clean = sanitizeForStorage(values, dropped) || {};
  result.photosDropped = dropped.photos;

  const record = {
    v: 1,
    intervention_id: String(interventionId),
    site_id: meta.siteId || null,
    label: meta.label || "",
    user_id: meta.userId || ownerUserId || null,
    saved_at: new Date().toISOString(),
    photos_dropped: dropped.photos,
    values: clean,
  };

  let raw;
  try {
    raw = JSON.stringify(record);
  } catch (_e) {
    return result;
  }
  // Un brouillon obèse ferait sauter le quota pour tout le monde, pointages
  // compris : on préfère ne pas l'écrire plutôt que d'évincer des heures.
  if (raw.length > DRAFT_MAX_BYTES) return result;

  try {
    store.setItem(draftKey(interventionId), raw);
    result.saved = true;
  } catch (_e) {
    /* quota atteint : pas de brouillon, et surtout pas de fausse promesse */
  }
  return result;
}

/**
 * Relit le brouillon d'une intervention.
 *
 * Retourne `null` quand il n'y en a pas, quand il appartient à un autre compte
 * (téléphone partagé) ou quand il a expiré — dans ces deux derniers cas la clé
 * est supprimée au passage.
 */
export function loadInterventionReportDraft(interventionId, { userId = ownerUserId } = {}) {
  if (!interventionId) return null;
  const store = storage();
  if (!store) return null;
  let record;
  try {
    const raw = store.getItem(draftKey(interventionId));
    if (!raw) return null;
    record = JSON.parse(raw);
  } catch (_e) {
    // Brouillon corrompu : on le retire au lieu de casser l'ouverture du
    // formulaire à chaque tentative.
    clearInterventionReportDraft(interventionId);
    return null;
  }
  if (!record || typeof record !== "object" || !record.values) {
    clearInterventionReportDraft(interventionId);
    return null;
  }
  if (userId && record.user_id && record.user_id !== userId) return null;

  const savedAt = record.saved_at ? new Date(record.saved_at) : null;
  const ageMs = savedAt ? Date.now() - savedAt.getTime() : 0;
  if (!savedAt || Number.isNaN(ageMs) || ageMs > DRAFT_MAX_AGE_MS) {
    clearInterventionReportDraft(interventionId);
    return null;
  }

  return {
    values: record.values,
    savedAt,
    ageMs: Math.max(0, ageMs),
    // « il y a X min » : on arrondit à la minute pleine, 0 = « à l'instant ».
    ageMinutes: Math.max(0, Math.floor(ageMs / 60000)),
    photosDropped: record.photos_dropped || 0,
    label: record.label || "",
    siteId: record.site_id || null,
  };
}

/** Supprime le brouillon — « Repartir de zéro » et soumission réussie. */
export function clearInterventionReportDraft(interventionId) {
  if (!interventionId) return false;
  cancelInterventionReportDraftSave(interventionId);
  const store = storage();
  if (!store) return false;
  try {
    store.removeItem(draftKey(interventionId));
    return true;
  } catch (_e) {
    return false;
  }
}

// Un timer par intervention : deux formulaires ouverts (deux onglets) ne
// doivent pas s'annuler l'un l'autre.
const draftTimers = new Map();

/**
 * Enregistrement différé (~1 s après la dernière frappe). À appeler à chaque
 * changement du formulaire ; l'écran doit appeler `flushInterventionReportDraftSave`
 * avant de se démonter, sinon la dernière seconde de saisie est perdue.
 */
export function scheduleInterventionReportDraftSave(interventionId, values, meta = {}) {
  if (!interventionId || typeof window === "undefined") return;
  const key = String(interventionId);
  const pending = draftTimers.get(key);
  if (pending) window.clearTimeout(pending.timer);
  const timer = window.setTimeout(() => {
    draftTimers.delete(key);
    saveInterventionReportDraft(interventionId, values, meta);
  }, DRAFT_DEBOUNCE_MS);
  draftTimers.set(key, { timer, values, meta });
}

/** Écrit immédiatement ce qui attendait (fermeture d'écran, mise en veille). */
export function flushInterventionReportDraftSave(interventionId) {
  if (!interventionId || typeof window === "undefined") return null;
  const key = String(interventionId);
  const pending = draftTimers.get(key);
  if (!pending) return null;
  window.clearTimeout(pending.timer);
  draftTimers.delete(key);
  return saveInterventionReportDraft(interventionId, pending.values, pending.meta);
}

/** Annule une écriture différée sans l'appliquer (brouillon supprimé). */
export function cancelInterventionReportDraftSave(interventionId) {
  if (!interventionId || typeof window === "undefined") return;
  const key = String(interventionId);
  const pending = draftTimers.get(key);
  if (!pending) return;
  window.clearTimeout(pending.timer);
  draftTimers.delete(key);
}

/**
 * Supprime les brouillons expirés. Sans ce ménage, chaque intervention
 * abandonnée laisserait sa clé à vie et le quota finirait par refuser les
 * pointages — qui, eux, valent des heures de paie.
 */
export function pruneInterventionReportDrafts() {
  const store = storage();
  if (!store) return 0;
  let removed = 0;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(INTERVENTION_REPORT_DRAFT_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      let savedAt = null;
      try {
        const parsed = JSON.parse(store.getItem(k) || "null");
        savedAt = parsed?.saved_at ? new Date(parsed.saved_at).getTime() : null;
      } catch (_e) {
        savedAt = null;
      }
      if (!savedAt || Number.isNaN(savedAt) || Date.now() - savedAt > DRAFT_MAX_AGE_MS) {
        store.removeItem(k);
        removed += 1;
      }
    }
  } catch (_e) {
    /* stockage inaccessible : rien à nettoyer */
  }
  return removed;
}

// ---------------------------------------------------------------------------
// File d'envoi des rapports
// ---------------------------------------------------------------------------

function readAllReports() {
  const store = storage();
  if (!store) return [];
  try {
    const raw = store.getItem(INTERVENTION_REPORT_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Une file corrompue ne doit pas casser l'écran maintenance.
    return Array.isArray(parsed)
      ? parsed.filter((it) => it && it.id && it.endpoint && it.intervention_id)
      : [];
  } catch (_e) {
    return [];
  }
}

function writeAllReports(items) {
  const store = storage();
  if (!store) return false;
  try {
    store.setItem(INTERVENTION_REPORT_QUEUE_STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch (_e) {
    return false;
  }
}

const reportListeners = new Set();

function notifyReports() {
  for (const fn of reportListeners) {
    try {
      fn();
    } catch (_e) {
      /* un abonné cassé ne doit pas empêcher les autres d'être prévenus */
    }
  }
}

export function subscribeInterventionReportQueue(fn) {
  if (typeof fn !== "function") return () => {};
  reportListeners.add(fn);
  return () => reportListeners.delete(fn);
}

/** Envois en attente de CET utilisateur, du plus ancien au plus récent. */
export function getInterventionReportQueue(userId = ownerUserId) {
  if (!userId) return [];
  return readAllReports()
    .filter((it) => it.user_id === userId)
    .sort(byCapturedAt);
}

/** Ce que l'utilisateur doit voir : « il me reste N rapports à envoyer ». */
export function countPendingInterventionReports(userId = ownerUserId) {
  return getInterventionReportQueue(userId).filter((it) => it.status !== "blocked").length;
}

/** Envois qu'un rejeu automatique ne débloquera pas : ils exigent un humain. */
export function getBlockedInterventionReports(userId = ownerUserId) {
  return getInterventionReportQueue(userId).filter((it) => it.status === "blocked");
}

function patchReportItem(id, patch) {
  const all = readAllReports();
  const idx = all.findIndex((it) => it.id === id);
  if (idx === -1) return false;
  all[idx] = { ...all[idx], ...patch };
  if (!writeAllReports(all)) return false;
  notifyReports();
  return true;
}

function dropReportItem(id) {
  const all = readAllReports();
  const next = all.filter((it) => it.id !== id);
  if (next.length === all.length) return false;
  if (!writeAllReports(next)) return false;
  notifyReports();
  return true;
}

/**
 * Met un envoi de rapport en file locale.
 *
 * Générique sur le modèle de `enqueuePunch` : l'appelant fournit l'endpoint
 * exact et le corps JSON, la file les rejoue tels quels au retour du réseau.
 * Elle sert donc aussi bien à la soumission d'un compte rendu qu'à l'envoi du
 * rapport au client.
 *
 * CONTRAINTE : `endpoint` doit être un chemin relatif de l'API (« /maintenance/… »).
 * Le rejeu part plus tard, hors du contexte de l'écran : accepter une URL
 * absolue reviendrait à poster le rapport — signatures du client comprises —
 * sur un hôte arbitraire.
 *
 * Lève si l'envoi ne peut pas être enregistré : mieux vaut dire « non
 * enregistré » que promettre à tort.
 */
export function enqueueInterventionReport({
  id,
  endpoint,
  method = "post",
  payload = {},
  interventionId,
  siteId,
  siteName,
  label,
  hadPhotos = false,
  userId,
  capturedAt,
} = {}) {
  if (!interventionId) throw new Error("Intervention manquante — envoi non enregistré");
  if (typeof endpoint !== "string" || !endpoint.startsWith("/") || endpoint.startsWith("//")) {
    throw new Error("Destination invalide — envoi non enregistré");
  }
  const verb = method === "patch" || method === "put" ? method : "post";
  const owner = userId || ownerUserId;
  if (!owner) throw new Error("Session introuvable — envoi non enregistré");

  // Texte seul : on écrase toute référence média pour qu'un appelant distrait
  // ne mette pas un fichier dans localStorage (quota) ni une URL locale morte.
  const dropped = { photos: 0 };
  const cleaned = sanitizeForStorage(payload, dropped) || {};
  let photosSeen = hadPhotos || dropped.photos > 0;
  for (const k of PHOTO_FIELD_KEYS) {
    if (Array.isArray(cleaned[k]) && cleaned[k].length > 0) photosSeen = true;
    if (k in cleaned) delete cleaned[k];
  }

  const at = capturedAt ? asDate(capturedAt) : new Date();
  const item = {
    id: id || newInterventionReportId(),
    user_id: owner,
    intervention_id: String(interventionId),
    site_id: siteId || null,
    site_name: siteName || "",
    label: label || "",
    endpoint,
    method: verb,
    payload: cleaned,
    had_photos: Boolean(photosSeen),
    captured_at: at.toISOString(),
    status: "pending",
    attempts: 0,
    next_attempt_at: 0,
    error: null,
  };

  let size = 0;
  try {
    size = JSON.stringify(item).length;
  } catch (_e) {
    throw new Error("Rapport trop complexe à enregistrer — envoi non enregistré");
  }
  if (size > REPORT_PAYLOAD_MAX_BYTES) {
    throw new Error("Rapport trop volumineux pour être mis en attente — envoi non enregistré");
  }

  const all = readAllReports();
  // Idempotence : le même identifiant client ne crée jamais deux envois.
  const sameId = all.find((it) => it.id === item.id);
  if (sameId) return sameId;
  // Déduplication du double appui : le même appel, sur la même intervention,
  // encore en attente, ne part pas deux fois — un rapport envoyé en double au
  // client, c'est un client qui appelle.
  // Le PAYLOAD entre dans la comparaison : sans lui, un technicien encore hors
  // réseau qui rouvre son rapport, corrige une valeur et resoumet recevait le
  // toast « enregistré » alors que c'est la PREMIÈRE version qui partait —
  // sa correction était perdue en silence. Même appel + payload différent =
  // on remplace l'entrée en attente par la version corrigée.
  const memePayload = JSON.stringify(item.payload || {});
  const idx = all.findIndex(
    (it) =>
      it.status !== "blocked" &&
      it.intervention_id === item.intervention_id &&
      it.endpoint === item.endpoint &&
      it.user_id === owner,
  );
  if (idx >= 0) {
    if (JSON.stringify(all[idx].payload || {}) === memePayload) return all[idx];
    all[idx] = { ...item, id: all[idx].id };
    if (!writeAllReports(all)) {
      throw new Error("Stockage de l'appareil saturé — envoi non enregistré");
    }
    notifyReports();
    return all[idx];
  }

  all.push(item);
  if (!writeAllReports(all)) {
    throw new Error("Stockage de l'appareil saturé — envoi non enregistré");
  }
  notifyReports();
  return item;
}

/**
 * Retire un envoi bloqué que l'utilisateur a lu et pris en charge. Un envoi
 * encore « pending » n'est jamais supprimable : rien ne disparaît sans qu'on
 * le décide.
 */
export function dismissInterventionReport(id) {
  const item = readAllReports().find((it) => it.id === id);
  if (!item || item.status !== "blocked") return false;
  return dropReportItem(id);
}

let flushingReports = false;

export function isInterventionReportQueueSyncing() {
  return flushingReports;
}

/**
 * Rejoue les envois de rapport en attente, du plus ancien au plus récent.
 *
 * Règles (identiques aux mémos) :
 * - un envoi ne sort de la file qu'après une réponse 2xx (axios lève sur tout
 *   le reste) : un envoi incertain reste en attente plutôt que d'être perdu ;
 * - deux appels concurrents ne peuvent pas partir en même temps (verrou) ;
 * - un refus métier (rapport non signé, contact client absent, droits retirés)
 *   passe en « blocked » : le rejouer indéfiniment n'y changerait rien.
 */
export async function flushInterventionReportQueue({ userId = ownerUserId, force = false } = {}) {
  const summary = { sent: 0, kept: 0, blocked: 0, skipped: false, offline: false };

  if (flushingReports) {
    summary.skipped = true;
    return summary;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    summary.offline = true;
    return summary;
  }

  const items = getInterventionReportQueue(userId).filter((it) => it.status === "pending");
  if (items.length === 0) return summary;

  flushingReports = true;
  notifyReports();
  try {
    for (const item of items) {
      if (!force && item.next_attempt_at && Date.now() < item.next_attempt_at) {
        summary.kept += 1;
        continue;
      }
      try {
        // `client_report_id` : clé d'idempotence côté client. Le backend
        // l'ignore aujourd'hui (Pydantic laisse tomber les champs inconnus) ;
        // elle est envoyée pour qu'il puisse dédoublonner sans changer le
        // contrat le jour où il la lira. En attendant, le garde-fou est la
        // règle « on ne retire de la file qu'après un 2xx ».
        await api[item.method](item.endpoint, {
          ...(item.payload || {}),
          client_report_id: item.id,
        });
        dropReportItem(item.id);
        summary.sent += 1;
      } catch (e) {
        const message = extractErrorMessage(e, "Envoi impossible pour le moment");
        const attempts = (item.attempts || 0) + 1;
        if (isRetriable(e)) {
          patchReportItem(item.id, {
            attempts,
            next_attempt_at: Date.now() + backoffMs(attempts),
            error: message,
            status: "pending",
          });
          summary.kept += 1;
          if (!e?.response) {
            // Plus de réseau du tout : inutile d'essayer les suivants.
            summary.offline = true;
            break;
          }
        } else {
          patchReportItem(item.id, { attempts, status: "blocked", error: message });
          summary.blocked += 1;
        }
      }
    }
  } finally {
    flushingReports = false;
    notifyReports();
  }
  return summary;
}

let reportAutoFlushInstalled = false;

/**
 * Installe les déclencheurs de vidage des rapports. Idempotent : appelé par
 * l'écran de saisie ET par l'Espace Maintenance, car aucun des deux n'est
 * garanti d'être monté quand le réseau revient.
 */
export function ensureInterventionReportQueueAutoFlush() {
  if (typeof window === "undefined" || reportAutoFlushInstalled) return;
  reportAutoFlushInstalled = true;

  // Ménage des brouillons abandonnés : une fois par session suffit.
  pruneInterventionReportDrafts();

  // Retour du réseau : on force, sans attendre le délai de repli.
  window.addEventListener("online", () => {
    flushInterventionReportQueue({ force: true });
  });

  // Le téléphone sort de la poche : c'est souvent là que le réseau est revenu.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flushInterventionReportQueue();
  });

  // Filet : `navigator.onLine` reste à true sur un réseau présent mais muet.
  window.setInterval(() => {
    flushInterventionReportQueue();
  }, AUTO_FLUSH_INTERVAL_MS);

  // Un autre onglet a soumis un rapport : on rafraîchit le compteur affiché ici.
  window.addEventListener("storage", (event) => {
    if (!event || event.key === null || event.key === INTERVENTION_REPORT_QUEUE_STORAGE_KEY) {
      notifyReports();
    }
  });

  // Un rapport peut dormir dans la file depuis la session précédente.
  flushInterventionReportQueue();
}
